"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronRight } from "lucide-react";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { StandingsCompact } from "@/views/home/partials/standings/StandingsCompact";
import { ArticlePyramid } from "@/views/home/partials/articles/ArticlePyramid";
import { InternationalStandingsCompact } from "@/views/standings/overrides/international/InternationalStandingsCompact";
import type { NewsBlogCompetitionSectionData } from "../types";
import { HighlightedMatches } from "./HighlightedMatches";
import { pickHighlightedMatches } from "../utils/pickHighlightedMatches";
import { SectionHeader, CTAButton } from "fansunited-sports-ui";
import { resolveEffectiveSidebarMode } from "./resolveEffectiveSidebarMode";
export interface NewsBlogCompetitionSectionProps {
  data: NewsBlogCompetitionSectionData;
}

/**
 * NewsBlogCompetitionSection - Renders a single competition section on the news-blog home page.
 *
 * Layout:
 * - Competition header with logo, name, and links to Standings and Matches pages
 * - First 3 articles in pyramid layout (1 on first row, 2 on second row) with Standings sidebar
 * - Basic competition stats
 *
 * Must be wrapped in NavigationProvider by parent component.
 */
export function NewsBlogCompetitionSection(t0) {
  const $ = _c(97);
  const {
    data
  } = t0;
  const labels = useLabels();
  const nav = useNavigation();
  const {
    nativeId,
    label,
    logo,
    articles,
    standings,
    upcomingMatches
  } = data;
  const resolvedLogo = logo;
  let t1;
  if ($[0] !== articles) {
    t1 = articles.slice(0, 6);
    $[0] = articles;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pyramidArticles = t1;
  let effectiveSidebar;
  let highlightedMatches;
  let t2;
  if ($[2] !== data.sidebar || $[3] !== nativeId || $[4] !== nav || $[5] !== standings || $[6] !== upcomingMatches) {
    const flatEntries = standings.flatMap(_temp);
    const isMultiGroup = standings.length > 1;
    effectiveSidebar = resolveEffectiveSidebarMode({
      mode: data.sidebar,
      standingsCount: flatEntries.length,
      highlightedMatchesCount: upcomingMatches.length
    });
    const standingsLimit = effectiveSidebar === "standings-only" ? 20 : 10;
    const matchesLimit = effectiveSidebar === "matches-only" ? 10 : 3;
    highlightedMatches = pickHighlightedMatches(upcomingMatches, flatEntries, matchesLimit);
    t2 = flatEntries.length > 0 ? isMultiGroup ? <InternationalStandingsCompact groups={standings} showHeader={false} standingsUrl={nav.standings(nativeId)} /> : <StandingsCompact standings={flatEntries.slice(0, standingsLimit)} showHeader={false} standingsUrl={nav.standings(nativeId)} /> : null;
    $[2] = data.sidebar;
    $[3] = nativeId;
    $[4] = nav;
    $[5] = standings;
    $[6] = upcomingMatches;
    $[7] = effectiveSidebar;
    $[8] = highlightedMatches;
    $[9] = t2;
  } else {
    effectiveSidebar = $[7];
    highlightedMatches = $[8];
    t2 = $[9];
  }
  const standingsBlock = t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      typography: {
        name: "text-sm font-semibold text-foreground"
      }
    };
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== labels.homePage.featuredMatches) {
    t4 = {
      name: labels.homePage.featuredMatches,
      styles: t3
    };
    $[11] = labels.homePage.featuredMatches;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = labels.homePage.allFixtures;
  let t6;
  if ($[13] !== nativeId || $[14] !== nav) {
    t6 = nav.matchesList(nativeId);
    $[13] = nativeId;
    $[14] = nav;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      container: "text-sm font-medium text-accent hover:text-accent/80 border-none p-0"
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== labels.homePage.allFixtures || $[18] !== t6) {
    t8 = <CTAButton text={t5} href={t6} variant="custom" styles={t7} />;
    $[17] = labels.homePage.allFixtures;
    $[18] = t6;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      container: "mb-2"
    };
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t4 || $[22] !== t8) {
    t10 = <SectionHeader entity={t4} primaryCta={t8} styles={t9} />;
    $[21] = t4;
    $[22] = t8;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== highlightedMatches) {
    t11 = <HighlightedMatches matches={highlightedMatches} />;
    $[24] = highlightedMatches;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11) {
    t12 = <div>{t10}{t11}</div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const matchesBlock = t12;
  let t13;
  if ($[29] !== label || $[30] !== nativeId || $[31] !== nav || $[32] !== resolvedLogo) {
    t13 = resolvedLogo && <Link href={nav.footballCompetition(nativeId)} prefetch={false} className="shrink-0"><Image src={resolvedLogo} alt={label} width={128} height={128} className="w-20 h-20 rounded-md object-contain" /></Link>;
    $[29] = label;
    $[30] = nativeId;
    $[31] = nav;
    $[32] = resolvedLogo;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== nativeId || $[35] !== nav) {
    t14 = nav.footballCompetition(nativeId);
    $[34] = nativeId;
    $[35] = nav;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== label) {
    t15 = <span className="truncate">{label}</span>;
    $[37] = label;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ChevronRight className="w-4 h-4 shrink-0" />;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t14 || $[41] !== t15) {
    t17 = <Link href={t14} prefetch={false} className="flex items-center gap-1 text-lg font-bold link-themed hover:underline">{t15}{t16}</Link>;
    $[40] = t14;
    $[41] = t15;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== nativeId || $[44] !== nav) {
    t18 = nav.footballCompetition(nativeId);
    $[43] = nativeId;
    $[44] = nav;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== labels.teamPage.tabs.overview || $[47] !== t18) {
    t19 = <Link href={t18} prefetch={false} className="link-neutral hover:underline">{labels.teamPage.tabs.overview}</Link>;
    $[46] = labels.teamPage.tabs.overview;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span className="text-muted-foreground">·</span>;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== nativeId || $[51] !== nav) {
    t21 = nav.standings(nativeId);
    $[50] = nativeId;
    $[51] = nav;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== labels.competitions.standings || $[54] !== t21) {
    t22 = <Link href={t21} prefetch={false} className="link-neutral hover:underline">{labels.competitions.standings}</Link>;
    $[53] = labels.competitions.standings;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span className="text-muted-foreground">·</span>;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== nativeId || $[58] !== nav) {
    t24 = nav.matchesList(nativeId);
    $[57] = nativeId;
    $[58] = nav;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== labels.matches.title || $[61] !== t24) {
    t25 = <Link href={t24} prefetch={false} className="link-neutral hover:underline">{labels.matches.title}</Link>;
    $[60] = labels.matches.title;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <span className="text-muted-foreground">·</span>;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== nativeId || $[65] !== nav) {
    t27 = nav.teamsList(nativeId);
    $[64] = nativeId;
    $[65] = nav;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== labels.teams.title || $[68] !== t27) {
    t28 = <Link href={t27} prefetch={false} className="link-neutral hover:underline">{labels.teams.title}</Link>;
    $[67] = labels.teams.title;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span className="text-muted-foreground">·</span>;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== nativeId || $[72] !== nav) {
    t30 = nav.competitionNews(nativeId);
    $[71] = nativeId;
    $[72] = nav;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== labels.teamPage.tabs.news || $[75] !== t30) {
    t31 = <Link href={t30} prefetch={false} className="link-neutral hover:underline">{labels.teamPage.tabs.news}</Link>;
    $[74] = labels.teamPage.tabs.news;
    $[75] = t30;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== t19 || $[78] !== t22 || $[79] !== t25 || $[80] !== t28 || $[81] !== t31) {
    t32 = <div className="flex flex-wrap items-center gap-1 text-sm mt-1">{t19}{t20}{t22}{t23}{t25}{t26}{t28}{t29}{t31}</div>;
    $[77] = t19;
    $[78] = t22;
    $[79] = t25;
    $[80] = t28;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] !== t17 || $[84] !== t32) {
    t33 = <div className="flex flex-col min-w-0">{t17}{t32}</div>;
    $[83] = t17;
    $[84] = t32;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  let t34;
  if ($[86] !== t13 || $[87] !== t33) {
    t34 = <div className="flex items-center gap-2">{t13}{t33}</div>;
    $[86] = t13;
    $[87] = t33;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  let t35;
  if ($[89] !== effectiveSidebar || $[90] !== matchesBlock || $[91] !== pyramidArticles || $[92] !== standingsBlock) {
    t35 = pyramidArticles.length > 0 && (effectiveSidebar === "none" ? <ArticlePyramid articles={pyramidArticles} /> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-4"><div className="lg:col-span-2"><ArticlePyramid articles={pyramidArticles} heroSizes="(max-width: 1024px) 100vw, 66vw" /></div><div className="space-y-4">{effectiveSidebar === "standings-only" && standingsBlock}{effectiveSidebar === "matches-only" && matchesBlock}{effectiveSidebar === "standings-and-matches" && <>{standingsBlock}{matchesBlock}</>}</div></div>);
    $[89] = effectiveSidebar;
    $[90] = matchesBlock;
    $[91] = pyramidArticles;
    $[92] = standingsBlock;
    $[93] = t35;
  } else {
    t35 = $[93];
  }
  let t36;
  if ($[94] !== t34 || $[95] !== t35) {
    t36 = <section className="space-y-4">{t34}{t35}</section>;
    $[94] = t34;
    $[95] = t35;
    $[96] = t36;
  } else {
    t36 = $[96];
  }
  return t36;
}
function _temp(g) {
  return g.entries;
}